import {Component, OnDestroy, OnInit} from '@angular/core';
import {GridOptions, GridReadyEvent} from 'ag-grid';
import {ValueGetterParams} from 'ag-grid/dist/lib/entities/colDef';
import {SweetAlertHelper} from 'app/core/helpers/sweet-alert-helper.service';
import {IBatchcredit, IBatchCreditInfo} from 'app/core/interfaces/posting.interface';
import {PostingService} from 'app/core/services/posting.service';
import {getSelectNodeAfterFilter} from 'app/utils/helpers';
import * as moment from 'moment';
import {Subscription} from 'rxjs/Subscription';

@Component({
  selector: 'app-grid-posting-batch',
  templateUrl: './grid-posting-batch.component.html',
  styleUrls: ['./grid-posting-batch.component.scss'],
})

export class GridPostingBatchComponent implements OnInit, OnDestroy {
  tabExceptions: 'readyPost' | 'denials' | 'exceptions' = 'readyPost';
  subscriptions: Subscription[] = [];
  batchCreditInfo: IBatchCreditInfo;
  gridData: IBatchcredit[] = [];
  gridOptions: GridOptions;
  readOnlyMode = false;

  constructor(
    private postingService: PostingService,
    private sweetAlertHelper: SweetAlertHelper
  ) {
    this.createGrid();
  }

  ngOnInit() {
    this.subscribeObservables();
  }

  subscribeObservables() {
    this.subscriptions.push(
      this.postingService.tabExceptions$.subscribe(
        (resp: 'readyPost' | 'exceptions' | 'denials') => {
          if (resp) {
            this.tabExceptions = resp;
            this.switchGridTabs();
          }
        }
      )
    );
    this.subscriptions.push(
      this.postingService.batchCreditList$.subscribe(
        (resp: IBatchCreditInfo) => {
          if (resp) {
            this.sendCountTabs(resp);
            this.batchCreditInfo = resp;
            this.switchGridTabs();
          } else {
            this.gridData = [];
            this.batchCreditInfo = null;
          }
        },
        (error) => {
          this.sweetAlertHelper.captureException(error);
        }
      )
    );
    this.subscriptions.push(
      this.postingService.clearFilterGrid$.subscribe((resp) => {
        if (resp && resp.id === 2) {
          this.gridOptions.api.setFilterModel(null);
        }
      })
    );

    this.subscriptions.push(
      this.postingService.clearFilterGrid$.subscribe((resp) => {
        if (resp && resp.id === 3) {
           this.gridOptions.api.setSortModel(null);
        }
      })
    );

    this.subscriptions.push(
      this.postingService.saveSubject$.subscribe((save: boolean) => {
        if (save) {
          this.gridData = [];
        }
      })
    );

    this.subscriptions.push(
      this.postingService.enableReadOnlyMode$.subscribe({
        next: (res: boolean) => {
          if (res) {
            this.readOnlyMode = true;
          } else {
            this.readOnlyMode = false;
          }

          if (this.gridOptions.columnApi) {
            this.gridOptions.columnApi.setColumnVisible('batchcreditid', !res);
          }
        },
        error: (error: any) => {
          this.sweetAlertHelper.captureException(error);
        }
      })
    );
  }

  sendCountTabs(resp) {
    if (resp.batchcredits) {
      this.objSend('readyPost', resp.batchcredits.length);
    }

    if (resp.batchcreditsdenials) {
      this.objSend('denials', resp.batchcreditsdenials.length);
    }

    if (resp.batchcreditsexception) {
      this.objSend('exceptions', resp.batchcreditsexception.length);
    }
  }

  objSend(tab, count) {
    this.postingService.onCountTabs({
      tab: tab,
      count: count
    });
  }

  createGrid() {
    this.gridOptions = <GridOptions> {
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      rowSelection: 'multiple',
      getRowStyle: this.getRowStyle,
      columnDefs: [
        {
          colId: 'batchcreditid',
          checkboxSelection: true,
          headerCheckboxSelection: true,
          width: 60
        },
        {
          headerName: 'Seq',
          field: 'addedon',
          width: 135,
          cellRenderer: (params) => {
            if (params && params.data) {
              return (this.gridData.length - params.rowIndex);
            }
          },
          cellStyle: { 'text-align': 'end' },
        },
        {
          headerName: 'Reservation',
          field: 'pnc',
          width: 250,
          cellRenderer: (params) => {
            if (params && params.data) {
              return String(params.data.pnc).slice(2);
            }
          },
          valueGetter(params: ValueGetterParams) {
            if (params && params.data) {
              return String(params.data.pnc).slice(2);
            }
          },
          cellStyle: { 'text-align': 'end' },
        },
        {
          headerName: 'DOS',
          field: 'servdate',
          width: 240,
          valueGetter: (params) => {
            if (params && params.data.servdate) {
              return moment(params.data.servdate).format('MM-DD-YYYY');
            }
          },
          cellStyle: { 'text-align': 'end' },
        },
        {
          headerName: 'Name',
          field: 'name',
          width: 350,

        },
        {
          headerName: 'Payor',
          field: 'payordescription',
          width: 500,
        },
        {
          headerName: 'Billed',
          field: 'billed',
          cellStyle: { 'text-align': 'end' },
          width: 200,
          cellRenderer: (params) => {
            const decimal = 0;
            return params && params.data.billed ? '$' + params.data.billed.toFixed(2) : '$' + decimal.toFixed(2);
          },
          valueGetter: (params) => {
            const decimal = 0;
            return params && params.data.billed ? params.data.billed.toFixed(2) : decimal.toFixed(2);
          },
        },
        {
          headerName: 'Amount',
          field: 'allowed',
          width: 210,
          cellStyle: { 'text-align': 'end' },
          cellRenderer: (params) => {
            const decimal = 0;
            return params && params.data.allowed ? '$' + params.data.allowed.toFixed(2) : '$' + decimal.toFixed(2);
          },
          valueGetter: (params) => {
            const decimal = 0;
            return params && params.data.allowed ? params.data.allowed.toFixed(2) : decimal.toFixed(2);
          },
        },
        {
          headerName: 'Procedure',
          field: 'procd',
          width: 200,
        },
        {
          headerName: 'Claim Nbr',
          field: 'icn',
          width: 340,
        },

        {
          headerName: 'Type Credit',
          field: 'credittypedescription',
          width: 240,
        },
        {
          headerName: 'Code Denial',
          field: 'denialcode',
          width: 240,
        },
        {
          headerName: 'Denial Reason',
          field: 'denialcodedescription',
          width: 650,
        },
        {
          headerName: 'User',
          cellStyle: { 'text-align': 'start' },
          field: 'nameadminuser',
        },
        {
          headerName: 'Is Closed',
          field: 'isclosed',
          width: 140,
          cellRenderer: (params) => {
           let ico = '';
           let billingStatus = null;
            if (params.data && params.data.isclosed) {
              ico = 'fa icofont-lock color-danger';
              billingStatus = 'Billing status closed';
            }
            return billingStatus && `<center><i class="${ico}" aria-hidden="true" title="${billingStatus}"></i></center>`;
          }
        }
      ],
      onGridReady: (event: GridReadyEvent) => {
        if (event) {
          this.gridOptions.api.sizeColumnsToFit();
        }
      },
      onSelectionChanged: () => {
        this.onSelectionChanged();
      },
      onFilterChanged: (params) => {
        this.postingService.onClearFilterGrid({
          type: 'clearFilter',
          clear: params.api['filterManager'].advancedFilterPresent,
          id: 1
        });
      },
      onSortChanged: () => {
        const sort =  this.gridOptions.api.getSortModel();
        this.postingService.onClearFilterGrid({
          type: 'clearSort',
          clear: sort.length ? true : false,
          id: 4
        });
      }
    };
  }

  getRowStyle(params: any): any {
    const OBJ_STYLE_ROW = {
      'background-color': '#ffffff',
      color: '#000000',
      cursor: 'default',
    };

    if (params.data && params.data.isclosed) {
      OBJ_STYLE_ROW['background-color'] = '#ffb2b2';
      OBJ_STYLE_ROW['border'] = '1px solid #ff837b';
    }

    if (params.data && !params.data.tripid) {
      OBJ_STYLE_ROW['color'] = '#ff0000';
    }

    return OBJ_STYLE_ROW;
  }

  switchGridTabs() {
    this.postingService.setArrayBatchcreditIds([]);
    switch (this.tabExceptions) {
      case 'readyPost':
        if (this.batchCreditInfo) {
          if (
            (this.batchCreditInfo && this.batchCreditInfo.batchcredits.length) ||
            !this.batchCreditInfo.batchcredits.length
          ) {
            this.postingService.onCountTabs({
              tab: this.tabExceptions,
              count: this.batchCreditInfo.batchcredits.length
            });
            this.gridData = this.batchCreditInfo.batchcredits;
          }
        }
        break;
      case 'exceptions':
        if (this.batchCreditInfo) {
          if (
            (this.batchCreditInfo &&
              this.batchCreditInfo.batchcreditsexception.length) ||
            !this.batchCreditInfo.batchcreditsexception.length
          ) {
            this.postingService.onCountTabs({
              tab: this.tabExceptions,
              count: this.batchCreditInfo.batchcreditsexception.length
            });
            this.gridData = this.batchCreditInfo.batchcreditsexception;
          }
        }
        break;
      case 'denials':
        if (this.batchCreditInfo) {
          if (
            (this.batchCreditInfo && this.batchCreditInfo.batchcreditsdenials.length) ||
            !this.batchCreditInfo.batchcreditsdenials.length
          ) {
            this.postingService.onCountTabs({
              tab: this.tabExceptions,
              count: this.batchCreditInfo.batchcreditsdenials.length
            });
            this.gridData = this.batchCreditInfo.batchcreditsdenials;
          }
        }
        break;
      default:
        break;
    }
  }

  onRowDoublicked(event: any): void {
    if (event && event.data.tripid && !this.readOnlyMode) {
      this.postingService.sendDataGrid(event.data);
      this.postingService.onCreditEdit(null);
      this.postingService.modeEditCreditSubject.next({ isEditMode: true });
      this.getDataBatchCredit(event.data);
    } else if (this.readOnlyMode && this.tabExceptions !== 'exceptions') {
      this.postingService.sendDataReservation(event.data);
    } else {
      this.sweetAlertHelper.createCustomAlert({
        title: 'Attention',
        text: 'Read only content',
        type: 'info',
      });
    }
  }

  onSelectionChanged() {
    const selectedRows = getSelectNodeAfterFilter(this.gridOptions);
    const arraybatchcreditid = [];
    if (selectedRows && selectedRows.length) {
      selectedRows.forEach((row) => {
        if (row) {
          arraybatchcreditid.push(row.batchcreditid);
        }
      });
    }
    this.postingService.setArrayBatchcreditIds(arraybatchcreditid);
  }

  getDataBatchCredit(data: any): void {
    this.postingService.getCreditInfoById(data)
    .subscribe((resp: any) => {
      if (resp) {
        this.postingService.onCreditEdit(resp);
      }
    }, error => {
      this.sweetAlertHelper.captureException(error);
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/billing-general/posting/grid-posting-batch/grid-posting-batch.component.ts